// client/src/components/suggestions/SuggestionCarousel.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardActionArea, CardMedia, CardContent,
  Skeleton, Dialog, DialogContent, DialogActions, Button,
  IconButton, Chip, CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CheckIcon from '@mui/icons-material/Check';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const TYPE_SLUG = {
  games:        'game',
  consoles:     'console',
  lego:         'lego',
  movies:       'movie',
  music:        'music',
  books:        'book',
  'board-games':'board-game',
};

// API paths for fetching item detail and adding to wishlist
const API_PATH = {
  games:        { detail: '/games',        wishlist: '/game-wishlist' },
  consoles:     { detail: '/consoles',     wishlist: '/console-wishlist' },
  lego:         { detail: '/lego',         wishlist: '/lego-wishlist' },
  movies:       { detail: '/movies',       wishlist: '/movie-wishlist' },
  music:        { detail: '/music',        wishlist: '/music-wishlist' },
  books:        { detail: '/books',        wishlist: '/book-wishlist' },
  'board-games':{ detail: '/board-games',  wishlist: '/board-game-wishlist' },
};

// Extract a human-readable description for the item, depending on type
function getDescription(doc, collectionType) {
  if (!doc) return null;
  if (collectionType === 'movies')       return doc.plot || null;
  if (collectionType === 'consoles')     return doc.summary || doc.description || null;
  if (collectionType === 'board-games')  return doc.description || null;
  return null;
}

// Pick a handful of interesting fields to surface in the dialog
function getDetailFields(doc, collectionType) {
  const fields = [];
  if (doc.platform)       fields.push({ label: 'Platform',   value: doc.platform });
  if (doc.genre)          fields.push({ label: 'Genre',      value: doc.genre });
  if (doc.releaseDate)    fields.push({ label: 'Released',   value: String(doc.releaseDate).slice(0, 10) });
  if (doc.year)           fields.push({ label: 'Year',       value: doc.year });
  if (doc.director)       fields.push({ label: 'Director',   value: doc.director });
  if (doc.author)         fields.push({ label: 'Author',     value: doc.author });
  if (doc.artist)         fields.push({ label: 'Artist',     value: doc.artist });
  if (doc.setNum)         fields.push({ label: 'Set No.',    value: doc.setNum });
  if (doc.numPieces)      fields.push({ label: 'Pieces',     value: doc.numPieces });
  if (doc.bggId)          fields.push({ label: 'BGG ID',     value: doc.bggId });
  if (doc.minPlayers && doc.maxPlayers) {
    fields.push({ label: 'Players', value: `${doc.minPlayers}–${doc.maxPlayers}` });
  }
  if (doc.publisher)      fields.push({ label: 'Publisher',  value: doc.publisher });
  if (doc.isbn13)         fields.push({ label: 'ISBN',       value: doc.isbn13 });
  if (doc.language)       fields.push({ label: 'Language',   value: doc.language });
  return fields.slice(0, 5);
}

function SuggestionDialog({ open, onClose, item, collectionType }) {
  const [detail, setDetail] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    if (!open || !item) return;
    setDetail(null);
    setAdded(false);
    setAddError('');
    const paths = API_PATH[collectionType];
    if (!paths || !item.itemId) { setDetail({}); return; }
    setFetching(true);
    axiosInstance.get(`${paths.detail}/${item.itemId}`)
      .then(res => setDetail(res.data))
      .catch(() => setDetail({}))
      .finally(() => setFetching(false));
  }, [open, item, collectionType]);

  const handleAddToWishlist = async () => {
    if (!item) return;
    const paths = API_PATH[collectionType];
    if (!paths) return;
    setAdding(true);
    setAddError('');
    try {
      const name = detail?.name || detail?.title || item.name || 'Unknown';
      const imageUrl = detail?.processedImageUrl || detail?.imageUrl || item.imageUrl || '';
      const body = { name, imageUrl };
      if (detail?.platform) body.platform = detail.platform;
      if (detail?.rawgId) body.rawgId = detail.rawgId;
      await axiosInstance.post(paths.wishlist, body);
      setAdded(true);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to add to wishlist';
      setAddError(msg);
    } finally {
      setAdding(false);
    }
  };

  if (!item) return null;

  const name = detail?.name || detail?.title || item.name || 'Unknown';
  const image = detail?.processedImageUrl || detail?.imageUrl || item.imageUrl;
  const fields = detail ? getDetailFields(detail, collectionType) : [];
  const description = getDescription(detail, collectionType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}>
            {item.reason === 'trending' ? 'Trending' : item.reason === 'from_catalog' ? 'From the catalogue' : item.reason === 'content_based' ? 'Matches your taste' : 'Recommended for you'}
          </Typography>
          {item.score != null && (
            <Chip label={`★ ${item.score.toFixed(1)}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {image && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src={image}
              alt={name}
              sx={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          </Box>
        )}

        {fetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {name}
            </Typography>

            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                {description}
              </Typography>
            )}

            {fields.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {fields.map(f => (
                  <Box key={f.label}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                      {f.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {f.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {addError && (
          <Typography variant="caption" color="error.main" sx={{ px: 0.5 }}>
            {addError}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} variant="text" size="small">Close</Button>
          <Button
            onClick={handleAddToWishlist}
            variant="contained"
            size="small"
            disabled={adding || added}
            startIcon={added ? <CheckIcon /> : <BookmarkAddIcon />}
          >
            {added ? 'Added to wishlist' : adding ? 'Adding…' : 'Add to wishlist'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default function SuggestionCarousel({ collectionType }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isFeatureEnabled('enableSuggestions')) { setLoading(false); return; }
    axiosInstance.get(`/suggestions/${collectionType}`)
      .then(res => setItems(res.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [collectionType, isFeatureEnabled]);

  if (!isFeatureEnabled('enableSuggestions')) return null;
  if (!loading && items.length === 0) return null;

  return (
    <>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}
          >
            You might like
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} sx={{ flexShrink: 0, width: 130 }}>
                  <Skeleton variant="rounded" width={130} height={100} />
                  <Skeleton variant="text" width="80%" sx={{ mt: 0.5 }} />
                  <Skeleton variant="text" width="50%" />
                </Box>
              ))
            : items.map(item => (
                <Card
                  key={item.itemId}
                  elevation={1}
                  sx={{ flexShrink: 0, width: 130 }}
                >
                  <CardActionArea onClick={() => setSelected(item)}>
                    {item.imageUrl ? (
                      <CardMedia
                        component="img"
                        height={100}
                        image={item.imageUrl}
                        alt={item.name}
                        sx={{ objectFit: 'contain', bgcolor: 'background.default', p: 0.5 }}
                      />
                    ) : (
                      <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                      </Box>
                    )}
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          fontWeight: 500,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.name || 'Unknown'}
                      </Typography>
                      {item.score != null && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          ★ {item.score.toFixed(1)}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
          }
        </Box>
      </Box>

      <SuggestionDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        collectionType={collectionType}
      />
    </>
  );
}
